import { defineComponent, reactive, onMounted, computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { fabric } from 'fabric';
import _ from 'lodash';
import { withLoading } from '@hems/component';
import IconButton from '@hems/component/src/mapper/button/IconButton.vue';
import MultiListDropdown from '@hems/component/src/mapper/dropdown/MultiListDropdown.vue';
import MapperTooltip from '@hems/component/src/mapper/tooltip/MapperTooltip.vue';
import MobileSelector from '@hems/component/src/smartmodule/gen2/MobileSelector.vue';
import MobileTimeLine from '@hems/component/src/smartmodule/gen2/timeline/MobileTimeLine.vue';
import { FabricHelperPro, SmartModuleHelperPro, MapperConstant as Constant } from '@hems/util';
import { INITIAL_POWER_UNIT, INITIAL_POWER_VALUE, MAPPER_BUTTON_TYPE, MAPPER_PERIOD_DATA, MAPPER_POWER_DEFAULT_VALUE, MODULE_LANDSCAPE_HEIGHT, MODULE_LANDSCAPE_WIDTH, getMapperBackgoundColor, } from '@hems/util/src/constant/mapper';
import { getColorPhaseRangeArray } from '@hems/util/src/helper/mapper/fabricHelperPro';
export default defineComponent({
    name: 'LogicalCanvas',
    components: {
        IconButton,
        MultiListDropdown,
        MapperTooltip,
        MobileSelector,
        MobileTimeLine,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        moduleDayPwData: {
            type: Array,
            required: true,
        },
        byDate: {
            type: String,
            default: MAPPER_PERIOD_DATA.DAY,
        },
        width: {
            type: Number,
            default: 0,
        },
        height: {
            type: Number,
            default: 0,
        },
        appType: {
            type: String,
            required: true,
        },
        totalPowerValue: {
            type: Object,
            default: () => MAPPER_POWER_DEFAULT_VALUE,
        },
        selectedDate: {
            type: String,
            default: '',
        },
        errorModules: {
            type: Array,
            default: () => [],
        },
        module15minPwData: {
            type: Array,
            default: () => [],
        },
        dropdownList: {
            type: Array,
            required: true,
        },
    },
    emits: ['close', 'changeLayoutMode', 'openCalendar', 'changeDateMode'],
    setup(props, { emit }) {
        const store = useStore();
        const [canvasWidth, canvasHeight] = SmartModuleHelperPro.getCanvasSize();
        const MOBILE_CANVAS_WIDTH = props.width ? props.width : canvasWidth;
        const MOBILE_CANVAS_HEIGHT = props.height ? props.height : canvasHeight;
        const INVERTER_TOP_POS = 237 - 44 - 10 - 24;
        const INVERTER_HEIGHT = 48;
        const INVERTER_WIDTH = 36;
        const LINE_HEIGHT = 54;
        const LOGICAL_CANVAS_MIN_LEFT_MARGIN = 10;
        const LOGICAL_CANVAS_MIN_TOP_MARGIN = 10;
        const LOGICAL_CANVAS_MODULE_MARGIN = 12;
        const isShowTooltip = ref(false);
        const showDropdown = ref(false);
        const activeSerial = ref('');
        const inverterSerial = ref('');
        const sliderTimeValue = ref('');
        const isDrag = ref(false);
        const undoredoCanvas = new fabric.Canvas('canvas');
        const canvas = ref(undoredoCanvas);
        const state = reactive({
            moduleList: props.data.deviceIdList,
            moduleDataListByCurrentSliderTime: [],
            dateType: props.byDate,
        });
        const groupedTimelineData = computed(() => _.chain(props.module15minPwData)
            .groupBy('colec_dt')
            .map((value, key) => ({
            colec_dt: FabricHelperPro.convertHourToMin(key),
            module_15min_pw_data_total: value.reduce((accumulator, moduleInfo) => accumulator + FabricHelperPro.roundDownEnergyToSecondDecimalPlaces(moduleInfo.watt), 0),
        }))
            .value());
        const colorCodeArray = ref(getColorPhaseRangeArray(state.dateType, props.selectedDate));
        const backgroundColor = getMapperBackgoundColor(store.state.appCtx.theme);
        const totalEnergyValue = computed(() => props.totalPowerValue);
        /** 선택한 모듈의 ColorIndex 계산 */
        const getColorCodeByActiveModule = () => {
            let modulePower;
            if (state.dateType === MAPPER_PERIOD_DATA.TIMELINE) {
                modulePower = FabricHelperPro.getPowerValue(state.moduleDataListByCurrentSliderTime, inverterSerial.value);
            }
            else {
                modulePower = FabricHelperPro.getPowerValue(props.moduleDayPwData, inverterSerial.value);
            }
            const colorIndex = FabricHelperPro.classifyValueToColorIndex(modulePower, colorCodeArray.value);
            return colorIndex;
        };
        function onClick(event) {
            initActiveSerial();
            FabricHelperPro.mobileButtonEvent(canvas.value, event);
        }
        function show() {
            showDropdown.value = true;
        }
        function hide() {
            showDropdown.value = false;
        }
        const changeDateMode = (value) => {
            state.dateType = value;
            colorCodeArray.value = getColorPhaseRangeArray(state.dateType, props.selectedDate);
            emit('changeDateMode', state.dateType);
        };
        onMounted(() => {
            init();
        });
        // logical canvas init
        function init() {
            canvas.value = new fabric.Canvas('canvas', {
                selection: false,
                width: MOBILE_CANVAS_WIDTH,
                height: MOBILE_CANVAS_HEIGHT,
                containerClass: 'inner_canvas',
                moveCursor: 'grabbing',
                hoverCursor: 'grab',
                fireRightClick: true,
            });
            // Disable context menu
            document.querySelectorAll('canvas').forEach((canvas) => {
                fabric.util.addListener(canvas, 'contextmenu', (e) => {
                    e.preventDefault();
                });
            });
            // 캔버스 이벤트 리스너 등록
            initEventHandler();
            initSVGElement();
        }
        function loadCanvasData() {
            withLoading(() => {
                try {
                    canvas.value.clear();
                    canvas.value.setBackgroundColor(backgroundColor, canvas.value.renderAll.bind(canvas.value));
                    loadFromJSON();
                    FabricHelperPro.lockDragObject(canvas.value);
                    FabricHelperPro.alignmentToCenter(canvas.value, canvas.value.width, canvas.value.height);
                    canvas.value.renderAll();
                }
                catch (e) {
                    console.log(e);
                }
            })();
        }
        function loadFromJSON() {
            createRootPannel();
            createPannels();
            drawLine();
            addPannelCount();
        }
        // 중앙 선 그리기
        function drawLine() {
            if (state.moduleList) {
                const lineStyle = {
                    stroke: '#8B8B8B',
                    strokeWidth: 1,
                    selectable: false,
                };
                // x값 중앙, y값은 (inverter top + inverter height) ~ (이전값 + 중앙선 height)
                const rootXPos = MOBILE_CANVAS_WIDTH / 2;
                const rootYPos = INVERTER_TOP_POS + INVERTER_HEIGHT;
                const rootToStringYPos = rootYPos + LINE_HEIGHT;
                const rootToString = new fabric.Line([rootXPos, rootYPos, rootXPos, rootToStringYPos], lineStyle);
                canvas.value.add(rootToString);
            }
        }
        // pannel 개별 하나 그리기
        function createPannel(group, left, top, moduleSerial, inverterSerial) {
            const Fabric = fabric;
            const objects = _.cloneDeep(svgObjectsPannel);
            const options = _.cloneDeep(svgOptionsPannel);
            // FIXME: groupSVGElements(): Object 이므로 부득이하게 타입 지정으로 처리. 추후 개선 필요
            const pannel = fabric.util.groupSVGElements(objects, options);
            const isError = props.appType === 'pro' && props.errorModules.some((errorModule) => errorModule.module_id === moduleSerial);
            // module day power data에서 개별 pannel serial No와 serial No 같은 값을 찾아서 pannel의 파워량 찾기.
            const modulePower = FabricHelperPro.getPowerValue(props.moduleDayPwData, inverterSerial);
            const modulePowerText = FabricHelperPro.convertEnergyUnit(modulePower).convertedEnergy;
            const moduleUnitText = FabricHelperPro.convertEnergyUnit(modulePower).unit;
            // pannel 별 컬러 코드
            const color = FabricHelperPro.getColorCode(modulePower, colorCodeArray.value, isError);
            const pannelOption = {
                type: 'group',
                originX: 'left',
                originY: 'bottom',
                qserial: moduleSerial,
                inverterSerial,
                qtype: 'pannel',
                borderColor: 'transparent',
                padding: 0,
                left,
                top,
                angle: 90,
                fill: '#00aa9e',
                subTargetCheck: false,
                lockMovementX: true,
                lockMovementY: true,
                hasControls: false,
            };
            pannel.set(pannelOption);
            pannel.setCoords();
            group.add(pannel);
            const powerText = new Fabric.Textbox(modulePowerText, {
                qtype: 'logical_text',
                type: 'textbox',
                originX: 'center',
                originY: 'bottom',
                textAlign: 'center',
                fill: '#fafafa',
                fontSize: 5,
                fontFamily: 'Pretendard',
                fontWeight: 'bold',
                lineHeight: 1,
                charSpacing: 100,
                lockScalingX: true,
                lockScalingY: true,
                hasControls: false,
                angle: 270,
                left: -1.5,
                top: 0,
            });
            const unitText = new Fabric.Text(moduleUnitText, {
                qtype: 'logical_text',
                type: 'text',
                originX: 'center',
                originY: 'bottom',
                textAlign: 'center',
                fill: '#fafafa',
                fontSize: 4,
                fontFamily: 'Pretendard',
                fontWeight: 'bold',
                lineHeight: 1.3,
                lockScalingX: true,
                lockScalingY: true,
                hasControls: false,
                angle: 270,
                left: 4.1,
                top: 0,
            });
            const serialnumText = new Fabric.Text('...'.concat(inverterSerial?.slice(-3)), {
                qtype: 'logical_text',
                type: 'textbox',
                originX: 'center',
                originY: 'bottom',
                textAlign: 'center',
                fill: '#fafafa',
                fontSize: 4,
                fontFamily: 'Pretendard',
                fontWeight: 'bold',
                lineHeight: 1,
                charSpacing: 100,
                lockScalingX: true,
                lockScalingY: true,
                hasControls: false,
                angle: 270,
                left: 11.3,
                top: 0,
            });
            pannel.add(powerText);
            pannel.add(unitText);
            pannel.add(serialnumText);
            const textShadow = new Fabric.RoundedRect({
                originX: 'center',
                originY: 'bottom',
                angle: 270,
                width: MODULE_LANDSCAPE_WIDTH,
                height: MODULE_LANDSCAPE_HEIGHT / 3,
                strokeWidth: 0,
                fill: Constant.getMapperRoundedRectColor(isError),
                opacity: 0.4,
                left: 12,
            });
            pannel.add(textShadow);
            textShadow.sendToBack();
            const rect = new fabric.Rect({
                type: 'rect',
                originX: 'center',
                originY: 'center',
                width: MODULE_LANDSCAPE_WIDTH,
                height: MODULE_LANDSCAPE_HEIGHT,
                angle: 90,
                strokeWidth: 0,
                rx: 2,
                ry: 2,
                fill: color ?? '#00aa9e',
                hasControls: false,
            });
            pannel.add(rect);
            rect.sendToBack();
            canvas.value.renderAll();
        }
        // 전체 pannel group 생성
        function createPannels() {
            if (state.moduleList) {
                const maxPvLen = state.moduleList.length;
                // group(array) width, height : box margin 제외한 값
                // group height -> 4의 배수 : len/4의 몫, 4의 배수 아닐때 : len/4의 몫+1
                // group width -> module width * 4
                // array width :
                // 총 개수 < 4 -> (module width + module left margin) * maxLen - module left margin,
                // 총 개수 >= 4 -> 4개로 고정이므로 (module width + module left margin) * 4 - module left margin,
                // array height :
                // 총 개수가 4의 배수이면 (module height + module top margin) * 몫 - module top margin,
                // 총 개수가 4의 배수가 아니면 (module height + module top margin) * (몫 + 1) - module top margin
                const array_width = (MODULE_LANDSCAPE_WIDTH + LOGICAL_CANVAS_MIN_LEFT_MARGIN) * (maxPvLen < 4 ? maxPvLen : 4) -
                    LOGICAL_CANVAS_MIN_LEFT_MARGIN;
                const array_height = (MODULE_LANDSCAPE_HEIGHT + LOGICAL_CANVAS_MIN_TOP_MARGIN) *
                    (maxPvLen % 4 != 0 ? Math.floor(maxPvLen / 4) + 1 : maxPvLen / 4) -
                    LOGICAL_CANVAS_MIN_TOP_MARGIN;
                const arrayOption = {
                    qtype: 'array',
                    originX: 'center',
                    originY: 'top',
                    borderColor: 'transparent',
                    left: MOBILE_CANVAS_WIDTH / 2,
                    top: INVERTER_TOP_POS + INVERTER_HEIGHT + LINE_HEIGHT,
                    width: array_width,
                    height: array_height,
                    subTargetCheck: true,
                    backgroundColor: 'transparent',
                };
                // FIXME: new fabric.Group(): Object 이므로 부득이하게 타입 지정으로 처리. 추후 개선 필요
                const group = new fabric.Group();
                group.set(arrayOption);
                canvas.value.add(group);
                // pannel box object
                // width, height : 마진 * 2를 더한 값
                group.addWithUpdate(new fabric.Rect({
                    // FIXME: 불필요해보임. 추후 삭제
                    // qtype: 'box',
                    originX: 'center',
                    originY: 'top',
                    left: MOBILE_CANVAS_WIDTH / 2,
                    top: INVERTER_TOP_POS + INVERTER_HEIGHT + LINE_HEIGHT,
                    width: array_width + LOGICAL_CANVAS_MODULE_MARGIN * 2,
                    height: array_height + LOGICAL_CANVAS_MODULE_MARGIN * 2,
                    fill: 'transparent',
                    stroke: '#8B8B8B',
                    strokeWidth: 1,
                    selectable: false,
                    hasControls: false,
                }));
                let moduleLeft = (array_width / 2) * -1;
                let moduleTop = (array_height / 2) * -1;
                state.moduleList.forEach((info, index) => {
                    // index + 1을 4로 나누기했을 때, 나머지가 1일 경우 top이랑 left 값 변경 후 생성
                    if (index + 1 > 2 && (index + 1) % 4 == 1) {
                        moduleLeft = (array_width / 2) * -1;
                        moduleTop += MODULE_LANDSCAPE_HEIGHT + LOGICAL_CANVAS_MIN_TOP_MARGIN;
                    }
                    // ac module id, mi id(tooltip에 들어갈 데이터) 햠께 전달
                    createPannel(group, moduleLeft, moduleTop, info.moduleId, info.deviceId);
                    moduleLeft += MODULE_LANDSCAPE_WIDTH + LOGICAL_CANVAS_MIN_LEFT_MARGIN;
                });
            }
        }
        // AC Combiner 생성
        function createRootPannel() {
            // TODO: 화면 로드 시 AC Combiner Skeleton Object 생성 작업 필요
            // const rect = new fabric.Rect({
            //   // qtype: 'inverter',
            //   originX: 'center',
            //   originY: 'top',
            //   left: MOBILE_CANVAS_WIDTH / 2,
            //   top: INVERTER_TOP_POS,
            //   width: INVERTER_WIDTH,
            //   height: INVERTER_HEIGHT,
            //   strokeWidth: 0,
            //   rx: 2,
            //   ry: 2,
            //   fill: 'lightgrey',
            //   lockMovementX: true,
            //   lockMovementY: true,
            //   hasControls: false,
            //   selectable: false,
            // });
            // canvas.value.add(rect);
            const { convertedEnergy, unit } = totalEnergyValue.value;
            const powerText = new fabric.Textbox(convertedEnergy, {
                fontSize: 6.5,
                fill: '#FAFAFA',
                originX: 'center',
                originY: 'bottom',
                textAlign: 'center',
                fontFamily: 'Pretendard',
                fontWeight: 600,
                left: MOBILE_CANVAS_WIDTH / 2,
                top: 193,
            });
            const unitText = new fabric.Text(unit, {
                fontSize: 4.5,
                fill: '#FAFAFA',
                originX: 'center',
                originY: 'bottom',
                fontFamily: 'Pretendard',
                fontWeight: 600,
                left: MOBILE_CANVAS_WIDTH / 2,
                top: 197,
            });
            const objects = _.cloneDeep(svgObjectsACCombiner);
            const options = _.cloneDeep(svgOptionsACCombiner);
            const acCombinerIcon = fabric.util.groupSVGElements(objects, options);
            acCombinerIcon.set({
                qtype: 'inverter',
                originX: 'center',
                originY: 'top',
                left: MOBILE_CANVAS_WIDTH / 2,
                top: INVERTER_TOP_POS,
                lockMovementX: true,
                lockMovementY: true,
                hasControls: false,
                selectable: false,
            });
            acCombinerIcon.scaleToWidth(INVERTER_WIDTH);
            acCombinerIcon.scaleToHeight(INVERTER_HEIGHT);
            acCombinerIcon.setCoords();
            const acCombinerGroup = new fabric.Group([acCombinerIcon, powerText, unitText], {
                qtype: 'inverter',
                hasControls: false,
                hasRotatingPoint: false,
                lockMovementX: true,
                lockMovementY: true,
            });
            canvas.value.add(acCombinerGroup);
        }
        // 모듈 아이콘 + 모둘 count
        function addPannelCount() {
            const maxPvLen = state.moduleList.length;
            const rect = new fabric.Rect({
                width: 36,
                height: 14,
                fill: '#F0F0F0',
                originX: 'center',
                originY: 'top',
                left: MOBILE_CANVAS_WIDTH / 2,
                top: INVERTER_TOP_POS + INVERTER_HEIGHT + 15,
                rx: 2,
                ry: 2,
            });
            const text = new fabric.Text(maxPvLen.toString(), {
                fontSize: 8,
                fill: '#8B8B8B',
                originX: 'center',
                originY: 'center',
                fontFamily: 'Pretendard',
                fontWeight: 700,
                left: MOBILE_CANVAS_WIDTH / 2 + 8,
                top: INVERTER_TOP_POS + INVERTER_HEIGHT + 15 + 7.5,
            });
            const objects = _.cloneDeep(svgObjectsPannelIcon);
            const options = _.cloneDeep(svgOptionsPannelIcon);
            const pannelIcon = fabric.util.groupSVGElements(objects, options);
            pannelIcon.set({
                lockMovementX: true,
                lockMovementY: true,
                hasControls: false,
                originX: 'center',
                originY: 'center',
                left: MOBILE_CANVAS_WIDTH / 2 - 8,
                top: INVERTER_TOP_POS + INVERTER_HEIGHT + 15 + 7.5,
            });
            pannelIcon.setCoords();
            const pannelCountInfo = new fabric.Group([rect, text, pannelIcon], {
                qtype: 'count',
                hasControls: false,
                hasRotatingPoint: false,
                lockMovementX: true,
                lockMovementY: true,
            });
            canvas.value.add(pannelCountInfo);
        }
        let svgObjectsPannel;
        let svgOptionsPannel;
        let svgObjectsACCombiner;
        let svgOptionsACCombiner;
        let svgObjectsPannelIcon;
        let svgOptionsPannelIcon;
        function initSVGElement() {
            const ACCombinerPath = require('@hems/component/resources/images/smartmodule/pro/ic_ac_combiner.svg');
            const pannelIconPath = require('@hems/component/resources/images/smartmodule/ic_module_layout.svg');
            const pannelPath = require('@hems/component/resources/images/smartmodule/module_icon.svg');
            fabric.loadSVGFromURL(ACCombinerPath, (objects, options) => {
                svgObjectsACCombiner = objects;
                svgOptionsACCombiner = options;
                loadCanvasData();
            });
            fabric.loadSVGFromURL(pannelIconPath, (objects, options) => {
                svgObjectsPannelIcon = objects;
                svgOptionsPannelIcon = options;
                loadCanvasData();
            });
            fabric.loadSVGFromURL(pannelPath, (objects, options) => {
                svgObjectsPannel = objects;
                svgOptionsPannel = options;
                loadCanvasData();
            });
        }
        // event
        function initEventHandler() {
            initCommonEventHandler();
            initMonitoringEventHandler();
        }
        function initCommonEventHandler() {
            canvas.value.on('mouse:wheel', (option) => {
                handlerMouseWheel(option.e);
            });
            document.addEventListener('touchstart', handlerTouchStart);
        }
        function initMonitoringEventHandler() {
            canvas.value.on('mouse:down', (option) => {
                handlerMonitoringMouseDown(option);
                showDropdown.value = false;
                isDrag.value = true;
            });
            canvas.value.on('mouse:up', () => {
                isDrag.value = false;
            });
            canvas.value.on('mouse:move', (option) => {
                if (option.target === null) {
                    if ('touches' in option.e) {
                        if (option.e.type === 'touchmove') {
                            const initPoint = {
                                x: option.pointer?.x ?? option.e.targetTouches[0].clientX,
                                y: option.pointer?.y ?? option.e.targetTouches[0].clientY,
                            };
                            handlerMonitoringTouchMove(option.e, initPoint);
                        }
                    }
                    else {
                        handlerMonitoringMouseMove(option.e);
                    }
                }
            });
        }
        function handlerMouseWheel(event) {
            // hideDeleteBtn -> edit mode
            // hideTooltip -> monitoring mode
            const delta = event.deltaY;
            FabricHelperPro.setZoomInOut(canvas.value, delta, event);
            initActiveSerial();
        }
        let fingerDist = 0;
        function handlerTouchStart(event) {
            if (event.touches.length === 2) {
                fingerDist = SmartModuleHelperPro.getFingerDistance(event);
            }
        }
        // mouse event -> tooltip + blur event
        function handlerMonitoringMouseDown(option) {
            tempLastClientX = -1;
            tempLastClientY = -1;
            if (SmartModuleHelperPro.isSelectObject(option)) {
                if (SmartModuleHelperPro.isArrayType(option.target) || SmartModuleHelperPro.isInverterType(option.target)) {
                    const selectObject = option.subTargets?.[0];
                    if (selectObject) {
                        if (SmartModuleHelperPro.isPannelType(selectObject)) {
                            // 선택된 obj 제외하고 blur 처리
                            FabricHelperPro.setActiveObject(canvas.value, selectObject);
                            // 선택된 pannel num update
                            updateActiveSerial();
                        }
                        else {
                            initActiveSerial();
                        }
                    }
                    else {
                        initActiveSerial();
                    }
                }
            }
            else {
                initActiveSerial();
            }
        }
        function handlerMonitoringMouseMove(mouseEvent) {
            if (isDrag.value && mouseEvent && mouseEvent.clientX && mouseEvent.clientY) {
                moveEvent(mouseEvent.clientX, mouseEvent.clientY);
            }
        }
        function handlerMonitoringTouchMove(touchEvent, point) {
            if (touchEvent?.touches) {
                const singleTouchEvent = touchEvent.touches[0];
                moveEvent(singleTouchEvent.clientX, singleTouchEvent.clientY, touchEvent, point);
            }
        }
        // canvas panning을 위한 임시 값
        let tempLastClientX = -1;
        let tempLastClientY = -1;
        let pinchZoom = 1;
        function moveEvent(clientX, clientY, touchEvent, point) {
            if (touchEvent && touchEvent.touches && touchEvent.touches.length > 1) {
                const newFingerDist = SmartModuleHelperPro.getFingerDistance(touchEvent);
                pinchZoom = pinchZoom * Math.abs(newFingerDist / fingerDist);
                fingerDist = newFingerDist;
                if (pinchZoom > Constant.MAX_ZOOM) {
                    pinchZoom = Constant.MAX_ZOOM;
                }
                if (pinchZoom < Constant.MIN_ZOOM) {
                    pinchZoom = Constant.MIN_ZOOM;
                }
                canvas.value.zoomToPoint({ x: point?.x || 0, y: point?.y || 0 }, pinchZoom);
                touchEvent.preventDefault();
                touchEvent.stopPropagation();
            }
            else {
                let deltaX = 0;
                let deltaY = 0;
                if (tempLastClientX !== -1) {
                    deltaX = clientX - tempLastClientX;
                }
                if (tempLastClientY !== -1) {
                    deltaY = clientY - tempLastClientY;
                }
                tempLastClientX = clientX;
                tempLastClientY = clientY;
                canvas.value.relativePan(new fabric.Point(deltaX, deltaY));
            }
        }
        function updateActiveSerial() {
            const activeObject = canvas.value.getActiveObject();
            if (activeObject) {
                if (SmartModuleHelperPro.isPannelType(activeObject)) {
                    activeSerial.value = SmartModuleHelperPro.getSerialNumber(activeObject);
                    inverterSerial.value = SmartModuleHelperPro.getInverterNumber(activeObject);
                }
            }
        }
        function initActiveSerial() {
            activeSerial.value = '';
            inverterSerial.value = '';
            FabricHelperPro.unSelectActiveObject(canvas.value);
        }
        const updateModuleDataList = () => {
            state.moduleDataListByCurrentSliderTime =
                state.dateType === MAPPER_PERIOD_DATA.TIMELINE ? getTimelineModuleDataList() : props.moduleDayPwData;
        };
        const getTimelineModuleDataList = () => {
            const moduleDataByCurrentTime = props.module15minPwData?.filter((moduleData) => {
                const minUnitValue = FabricHelperPro.convertHourToMin(String(moduleData.colec_dt));
                if (minUnitValue === Number(sliderTimeValue.value))
                    return moduleData;
            });
            const resultData = moduleDataByCurrentTime?.map((moduleData) => {
                return {
                    device_id: moduleData.device_id,
                    total_power: moduleData.watt,
                };
            });
            return resultData;
        };
        const acCombinerPWByCurrentSliderTime = ref({
            origin: 0,
            convertedEnergy: INITIAL_POWER_VALUE,
            unit: INITIAL_POWER_UNIT,
        });
        const updateCombinerData = () => {
            if (state.dateType === MAPPER_PERIOD_DATA.TIMELINE) {
                const originData = groupedTimelineData.value?.find((timelineData) => timelineData.colec_dt === Number(sliderTimeValue.value))
                    ?.module_15min_pw_data_total || 0;
                acCombinerPWByCurrentSliderTime.value = {
                    origin: originData,
                    convertedEnergy: String(FabricHelperPro.roundDownEnergyToSecondDecimalPlaces(originData)),
                    unit: 'W',
                };
            }
            else {
                acCombinerPWByCurrentSliderTime.value = totalEnergyValue.value;
            }
        };
        const changeToTimeline = (timeValue) => {
            state.dateType = MAPPER_PERIOD_DATA.TIMELINE;
            sliderTimeValue.value = timeValue;
            updateModuleDataList();
            updateCombinerData();
            colorCodeArray.value = getColorPhaseRangeArray(state.dateType, props.selectedDate);
        };
        const changeToDay = () => {
            state.dateType = MAPPER_PERIOD_DATA.DAY;
            updateModuleDataList();
            updateCombinerData();
            emit('changeDateMode', state.dateType);
        };
        watch(() => state.moduleDataListByCurrentSliderTime, () => {
            FabricHelperPro.setPowerText(canvas.value, state.moduleDataListByCurrentSliderTime, props.errorModules, state.dateType, colorCodeArray.value, acCombinerPWByCurrentSliderTime.value);
        });
        watch(() => inverterSerial.value, (inverterSerial) => {
            isShowTooltip.value = !!(inverterSerial && inverterSerial !== '');
        });
        watch(() => [state.moduleList.length, props.moduleDayPwData.length], () => {
            initSVGElement();
        });
        return {
            _,
            state,
            store,
            initActiveSerial,
            show,
            hide,
            initSVGElement,
            onClick,
            getColorCodeByActiveModule,
            changeDateMode,
            changeToTimeline,
            changeToDay,
            totalEnergyValue,
            acCombinerPWByCurrentSliderTime,
            isShowTooltip,
            activeSerial,
            inverterSerial,
            groupedTimelineData,
            MAPPER_BUTTON_TYPE,
        };
    },
});
